import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTitleHidden } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Mail, Lock, User, Chrome, Eye, EyeOff, Check } from 'lucide-react';
import { toast } from 'sonner';
import { TwoFactorInline } from './personal/TwoFactorModal';
import api from '../services/api';

export default function AuthModal({ open, onOpenChange, defaultTab = 'login', resetToken = null }) {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(!!resetToken);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetComplete, setResetComplete] = useState(false);
  const [show2FA, setShow2FA] = useState(false);
  const [pendingToken, setPendingToken] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [suspiciousLogin, setSuspiciousLogin] = useState(false);
  const [suspiciousReasons, setSuspiciousReasons] = useState([]);
  const { login, complete2FALogin, register, loginWithGoogle, error, setError } = useAuth();

  const API_URL = '';
  
  // Handle reset token from URL
  useEffect(() => {
    if (resetToken) {
      setShowResetPassword(true);
    }
  }, [resetToken]);

  // Update active tab when defaultTab changes
  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
    }
  }, [open, defaultTab]);

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form state
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [regConfirmPassword, setRegConfirmPassword] = useState('');

  // Password visibility state
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegPassword, setShowRegPassword] = useState(false);
  const [showRegConfirmPassword, setShowRegConfirmPassword] = useState(false);

  const resetForms = () => {
    setLoginEmail('');
    setLoginPassword('');
    setRegName('');
    setRegEmail('');
    setRegPassword('');
    setRegConfirmPassword('');
    setForgotEmail('');
    setShowForgotPassword(false);
    setResetSent(false);
    setNewPassword('');
    setConfirmNewPassword('');
    setShowResetPassword(false);
    setResetComplete(false);
    setError(null);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      toast.error('Please enter your email');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResetSent(true);
        toast.success('Password reset link sent to your email');
      } else {
        toast.error(data.detail || 'Failed to send reset email');
      }
    } catch (err) {
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmNewPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/v2/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: resetToken, new_password: newPassword })
      });

      const data = await response.json();
      
      if (response.ok) {
        setResetComplete(true);
        toast.success('Password reset successfully!');
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (err) {
      toast.error('Failed to reset password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      const result = await login(loginEmail, loginPassword);
      
      // Check if 2FA is required
      if (result?.requires_2fa) {
        setPendingToken(result.pending_token);
        setEmailHint(result.email_hint || loginEmail);
        setSuspiciousLogin(!!result.suspicious);
        setSuspiciousReasons(result.suspicious_reasons || []);
        setShow2FA(true);
        setIsLoading(false);
        return;
      }
      
      toast.success('Welcome back!');
      resetForms();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regName || !regEmail || !regPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (regPassword !== regConfirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (regPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsLoading(true);
    try {
      await register(regName, regEmail, regPassword);
      toast.success('Account created! You have a 14-day free trial.');
      resetForms();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    loginWithGoogle();
  };

  return (
    <>
    <Dialog open={show2FA ? true : open} onOpenChange={(value) => {
      if (show2FA && !value) return; // Don't close while 2FA is pending
      if (!value) resetForms();
      onOpenChange(value);
    }}>
      <DialogContent className="sm:max-w-[425px] p-0 overflow-hidden bg-slate-900 border-slate-700">
        <DialogTitleHidden>Authentication</DialogTitleHidden>
        {show2FA ? (
          // Inline 2FA verification (replaces login form)
          <TwoFactorInline
            pendingToken={pendingToken}
            emailHint={emailHint}
            suspicious={suspiciousLogin}
            suspiciousReasons={suspiciousReasons}
            onVerified={(data) => {
              setShow2FA(false);
              setSuspiciousLogin(false);
              setSuspiciousReasons([]);
              complete2FALogin(data);
              toast.success('Welcome back!');
              resetForms();
              onOpenChange(false);
            }}
            onCancel={() => {
              setShow2FA(false);
              setPendingToken('');
              setSuspiciousLogin(false);
              setSuspiciousReasons([]);
            }}
          />
        ) : showResetPassword ? (
          // Reset Password Form (from email link)
          <div className="p-6 space-y-4">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white">
                {resetComplete ? 'Password Reset!' : 'Create New Password'}
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                {resetComplete 
                  ? 'Your password has been changed successfully' 
                  : 'Enter your new password below'}
              </p>
            </div>

            {resetComplete ? (
              <div className="text-center space-y-4">
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="h-8 w-8 text-green-400 mx-auto mb-2">✓</div>
                  <p className="text-green-400 text-sm">
                    You can now sign in with your new password
                  </p>
                </div>
                <Button
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  onClick={() => {
                    setShowResetPassword(false);
                    setResetComplete(false);
                  }}
                >
                  Sign In
                </Button>
              </div>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password" className="text-slate-300">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white"
                      data-testid="new-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-3 text-slate-500 hover:text-slate-300 transition-colors"
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirm-new-password" className="text-slate-300">Confirm New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                    <Input
                      id="confirm-new-password"
                      type={showNewPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="pl-10 bg-slate-800 border-slate-700 text-white"
                      data-testid="confirm-new-password-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  disabled={isLoading}
                  data-testid="reset-password-btn"
                >
                  {isLoading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Resetting...</>
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              </form>
            )}
          </div>
        ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-slate-800">
            <TabsTrigger 
              value="login" 
              data-testid="auth-login-tab"
              className="data-[state=active]:bg-slate-700"
            >
              Sign In
            </TabsTrigger>
            <TabsTrigger 
              value="register" 
              data-testid="auth-register-tab"
              className="data-[state=active]:bg-slate-700"
            >
              Sign Up
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="p-6 space-y-4">
            {showForgotPassword ? (
              // Forgot Password Form
              <div className="space-y-4">
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white">Reset Password</h2>
                  <p className="text-slate-400 text-sm mt-1">
                    {resetSent 
                      ? 'Check your email for reset instructions' 
                      : 'Enter your email to receive a reset link'}
                  </p>
                </div>

                {resetSent ? (
                  <div className="text-center space-y-4">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                      <Mail className="h-8 w-8 text-green-400 mx-auto mb-2" />
                      <p className="text-green-400 text-sm">
                        We have sent a password reset link to <strong>{forgotEmail}</strong>
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      className="w-full border-slate-700 hover:bg-slate-800 text-white"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setResetSent(false);
                        setForgotEmail('');
                      }}
                    >
                      Back to Sign In
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="forgot-email" className="text-slate-300">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                        <Input
                          id="forgot-email"
                          type="email"
                          placeholder="your@email.com"
                          value={forgotEmail}
                          onChange={(e) => setForgotEmail(e.target.value)}
                          className="pl-10 bg-slate-800 border-slate-700 text-white"
                          data-testid="forgot-email-input"
                        />
                      </div>
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      disabled={isLoading}
                      data-testid="forgot-submit-btn"
                    >
                      {isLoading ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending...</>
                      ) : (
                        'Send Reset Link'
                      )}
                    </Button>

                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(false)}
                      className="w-full text-slate-400 hover:text-white text-sm transition-colors"
                    >
                      ← Back to Sign In
                    </button>
                  </form>
                )}
              </div>
            ) : (
              // Regular Login Form
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-white">Welcome Back</h2>
                  <p className="text-slate-400 text-sm mt-1">Sign in to sync your data</p>
                </div>

                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-slate-300">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="your@email.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="pl-10 bg-slate-800 border-slate-700 text-white"
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-slate-300">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                      <Input
                        id="login-password"
                        type={showLoginPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white"
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowLoginPassword(!showLoginPassword)}
                        className="absolute right-3 top-3 text-slate-500 hover:text-slate-300 transition-colors"
                        data-testid="login-password-toggle"
                      >
                        {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-orange-400 hover:text-orange-300 text-sm transition-colors"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    disabled={isLoading}
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? (
                      <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-700" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-slate-900 px-2 text-slate-500">Or continue with</span>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full border-slate-700 hover:bg-slate-800 text-white"
                  onClick={handleGoogleLogin}
                  data-testid="google-login-btn"
                >
                  <Chrome className="mr-2 h-4 w-4" />
                  Google
                </Button>
              </>
            )}
          </TabsContent>

          <TabsContent value="register" className="p-6 space-y-4">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-white">Create Account</h2>
              <p className="text-slate-400 text-sm mt-1">Start your 14-day free trial</p>
            </div>

            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="reg-name" className="text-slate-300">Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                  <Input
                    id="reg-name"
                    type="text"
                    placeholder="Your name"
                    value={regName}
                    onChange={(e) => setRegName(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white"
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-email" className="text-slate-300">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                  <Input
                    id="reg-email"
                    type="email"
                    placeholder="your@email.com"
                    value={regEmail}
                    onChange={(e) => setRegEmail(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white"
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                  <Input
                    id="reg-password"
                    type={showRegPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={regPassword}
                    onChange={(e) => setRegPassword(e.target.value)}
                    className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white"
                    data-testid="register-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegPassword(!showRegPassword)}
                    className="absolute right-3 top-3 text-slate-500 hover:text-slate-300 transition-colors"
                    data-testid="register-password-toggle"
                  >
                    {showRegPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {regPassword && (
                  <div className="grid grid-cols-2 gap-x-3 gap-y-0.5 pt-1" data-testid="password-checklist">
                    {[
                      { met: regPassword.length >= 12, label: '12+ characters' },
                      { met: /[A-Z]/.test(regPassword), label: '1 uppercase' },
                      { met: /[a-z]/.test(regPassword), label: '1 lowercase' },
                      { met: /[0-9]/.test(regPassword), label: '1 number' },
                      { met: /[!@#$%^&*()_+\-=[\]{}|;:'",.<>?/\\`~]/.test(regPassword), label: '1 special char' },
                    ].map(({ met, label }) => (
                      <span key={label} className={`flex items-center gap-1.5 text-[11px] transition-colors ${met ? 'text-emerald-400' : 'text-slate-600'}`} data-testid={`pw-check-${label.replace(/\s+/g, '-')}`}>
                        <Check className={`h-3 w-3 flex-shrink-0 ${met ? 'text-emerald-400' : 'text-slate-700'}`} />
                        {label}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="reg-confirm-password" className="text-slate-300">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
                  <Input
                    id="reg-confirm-password"
                    type={showRegConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={regConfirmPassword}
                    onChange={(e) => setRegConfirmPassword(e.target.value)}
                    className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white"
                    data-testid="register-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowRegConfirmPassword(!showRegConfirmPassword)}
                    className="absolute right-3 top-3 text-slate-500 hover:text-slate-300 transition-colors"
                    data-testid="register-confirm-password-toggle"
                  >
                    {showRegConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-orange-600 hover:bg-orange-700"
                disabled={isLoading}
                data-testid="register-submit-btn"
              >
                {isLoading ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creating account...</>
                ) : (
                  'Create Account'
                )}
              </Button>
            </form>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-slate-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-slate-900 px-2 text-slate-500">Or continue with</span>
              </div>
            </div>

            <Button
              variant="outline"
              className="w-full border-slate-700 hover:bg-slate-800 text-white"
              onClick={handleGoogleLogin}
              data-testid="google-register-btn"
            >
              <Chrome className="mr-2 h-4 w-4" />
              Google
            </Button>

            <p className="text-center text-xs text-slate-500 mt-4">
              By creating an account, you agree to our Terms of Service
            </p>
          </TabsContent>
        </Tabs>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
}
